/**
 * 入社申請で新規登録する従業員(新しい申請 › 入社申請の「対象者」段)。
 *
 * 入社申請の対象者は、まだ居ない人をその場で入力して従業員ごと作る(登録済みの人は選ばない)。
 * 送信は POST /api/hr/onboarding で、従業員と申請が 1 トランザクションで出来る(片方だけ残らない)。
 * 在籍状態は入社前に固定(サーバーも固定する)、入社日は「内容」段の入社日と兼ねる。
 *
 * ウィザードは段ごとに入力欄を出し入れするので、antd の Form ではなくページ側の state で持つ
 * (段を戻っても入力が消えないように)。入力欄は RequestForm.tsx の RequestNewEmployee。
 */
import { useMemo, useState } from "react";

import type { EmployeeInput } from "../../lib/api";
import { useCustomFields, useOrgUnits } from "./hooks";
import type { DraftEmployee } from "./requestDraft";

export type NewEmployee = {
  emp_code: string;
  family_name: string;
  given_name: string;
  family_name_kana: string;
  given_name_kana: string;
  email: string;
  employment_type?: string;
  work_mobile: string;
  work_phone: string;
  extension: string;
  org_unit_id?: number;
  position_id?: number;
  manager_id?: number | null;
  /** 段を戻ったときも氏名・社員番号で表示するための上長ラベル。API には送らない */
  manager_label?: string;
  note: string;
  /** カスタム項目(key ごと。日付は YYYY-MM-DD) */
  extra: Record<string, string>;
};

const EMPTY: NewEmployee = {
  emp_code: "",
  family_name: "",
  given_name: "",
  family_name_kana: "",
  given_name_kana: "",
  email: "",
  work_mobile: "",
  work_phone: "",
  extension: "",
  note: "",
  extra: {},
};

// 電話の形(サーバーの `phone_value` と同じ。全角はサーバーが半角に寄せる)。EmployeeForm の PHONE_RULES と同じ
export const PHONE_PATTERN = /^[0-9０-９+＋\-－ー()（） \u3000]*$/;

export function useNewEmployee() {
  const [value, setValue] = useState<NewEmployee>(EMPTY);
  const units = useOrgUnits(true);
  const customFields = useCustomFields();
  const fields = useMemo(() => customFields.data?.items ?? [], [customFields.data]);

  const set = (patch: Partial<NewEmployee>) => setValue((p) => ({ ...p, ...patch }));
  const setExtra = (key: string, v: string) => setValue((p) => ({ ...p, extra: { ...p.extra, [key]: v } }));

  const department = units.data?.items.find((u) => u.id === value.org_unit_id)?.path ?? "";

  // 申請の転記欄(emp:… / extra:…)に見せる「入力中の人」。id が無いので対象者の候補は引かない
  const draftEmployee: DraftEmployee = useMemo(
    () => ({
      name: `${value.family_name.trim()} ${value.given_name.trim()}`.trim(),
      emp_code: value.emp_code.trim(),
      email: value.email.trim(),
      status: "pre_onboard",
      family_name: value.family_name.trim(),
      given_name: value.given_name.trim(),
      family_name_kana: value.family_name_kana.trim(),
      given_name_kana: value.given_name_kana.trim(),
      department,
      employment_type: value.employment_type ?? "",
      extra: Object.fromEntries(fields.map((f) => [f.key, value.extra[f.key] ?? ""])),
    }),
    [value, department, fields],
  );

  /** 「次へ」で止める理由。揃っていれば undefined */
  const missing = (): string | undefined => {
    if (!value.family_name.trim()) return "姓は必須";
    if (!value.given_name.trim()) return "名は必須";
    if (!value.employment_type) return "契約形態は必須";
    for (const [k, label] of [
      ["work_mobile", "会社携帯"],
      ["work_phone", "会社電話"],
      ["extension", "内線"],
    ] as const) {
      if (!PHONE_PATTERN.test(value[k])) return `${label}は数字と + - ( ) 空白だけで書く`;
    }
    // 必須のカスタム項目が空だとサーバーが 400 で返す。申請の直前ではなくこの段で止める
    const req = fields.find((f) => f.required && !(value.extra[f.key] ?? "").trim());
    if (req) return `${req.label}は必須`;
    return undefined;
  };

  /** POST /api/hr/onboarding の employee。在籍状態・入社日はサーバーが固定・申請の入社日で埋める */
  const input = (joinedAt: string): EmployeeInput => ({
    // 空なら送らない = サーバーが契約形態から採番する
    ...(value.emp_code.trim() ? { emp_code: value.emp_code.trim() } : {}),
    family_name: value.family_name.trim(),
    given_name: value.given_name.trim(),
    family_name_kana: value.family_name_kana,
    given_name_kana: value.given_name_kana,
    email: value.email.trim(),
    employment_type: value.employment_type ?? "",
    work_mobile: value.work_mobile,
    work_phone: value.work_phone,
    extension: value.extension,
    status: "pre_onboard",
    org_unit_id: value.org_unit_id ?? 0,
    position_id: value.position_id ?? 0,
    manager_id: value.manager_id ?? 0,
    note: value.note,
    joined_at: joinedAt || null,
    extra: draftEmployee.extra,
  });

  return { value, set, setExtra, fields, draftEmployee, missing, input };
}

export type NewEmployeeState = ReturnType<typeof useNewEmployee>;
